import { Button } from '@/components/button'
import { Close } from '@/components/icons'
import { AutocompleteAddress, AutocompleteLocationInput } from '@/components/input'
import { RangeSlider } from '@/components/range-slider'
import { EventStatus } from '@/types'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  toggleFilter: () => void
  onClearAll: () => void
  eventStatus: EventStatus[]
  onFilterEventStatus: (type: EventStatus) => void
  location: AutocompleteAddress
  setGeometry: Dispatch<SetStateAction<{ lat: number; lng: number } | null>>
  onChangeLocation: (value: AutocompleteAddress) => void
  onGetPlaceDetail: (address: AutocompleteAddress) => Promise<void>
  distance: number
  tabActive: 'CLUBS' | 'EVENTS'
  onChangeDistance: (e: number) => void
}

export const Filter = ({
  toggleFilter,
  onClearAll,
  onFilterEventStatus,
  eventStatus,
  location,
  setGeometry,
  onGetPlaceDetail,
  distance,
  tabActive,
  onChangeDistance,
  onChangeLocation,
}: Props) => {
  const { t } = useTranslation()

  return (
    <div
      className={`fixed z-10 top-0 w-screen h-screen p-4 lg:p-0 bg-primary-400 lg:bg-transparent left-0 lg:left-auto lg:right-6 xl:right-9 lg:top-36 lg:w-[240px] xl:w-[280px] transition-all duration-500 ease-in-out flex flex-col gap-y-6`}
    >
      <div className="flex justify-between py-4">
        <div className="flex items-center gap-x-2">
          <Button onClick={toggleFilter} className="hover:opacity-50">
            <Close className="text-white" />
          </Button>
          <span className="text-xl font-semibold text-white">{t('common.filters')}</span>
        </div>

        <button className="text-sm px-1 text-white font-semibold" onClick={onClearAll}>
          {t('common.clearAll')}
        </button>
      </div>

      {tabActive === 'EVENTS' && (
        <div className="flex flex-col">
          <span className="text-white text-sm font-bold">{t('event.eventStatus')}</span>
          <div className="flex flex-wrap">
            <div
              className="flex items-center gap-x-3 p-4 cursor-pointer"
              onClick={() => onFilterEventStatus('UPCOMING')}
            >
              <input
                type="checkbox"
                className="w-5 h-5 cursor-pointer"
                checked={eventStatus.includes('UPCOMING')}
                onChange={() => onFilterEventStatus('UPCOMING')}
              />
              <span className="text-sm text-white">{t('common.upcoming')}</span>
            </div>
            <div
              className="flex items-center gap-x-3 p-4 cursor-pointer"
              onClick={() => onFilterEventStatus('COMPLETE')}
            >
              <input
                type="checkbox"
                className="w-5 h-5 cursor-pointer"
                checked={eventStatus.includes('COMPLETE')}
                onChange={() => onFilterEventStatus('COMPLETE')}
              />
              <span className="text-sm text-white">{t('common.completed')}</span>
            </div>
            <div
              className="flex items-center gap-x-3 p-4 cursor-pointer"
              onClick={() => onFilterEventStatus('IN_PROGRESS')}
            >
              <input
                type="checkbox"
                className="w-5 h-5 cursor-pointer"
                checked={eventStatus.includes('IN_PROGRESS')}
                onChange={() => onFilterEventStatus('IN_PROGRESS')}
              />
              <span className="text-sm text-white">{t('common.ongoing')}</span>
            </div>
          </div>
        </div>
      )}

      <AutocompleteLocationInput
        label={t('common.location')}
        placeholder={t('common.location')}
        value={location}
        setValue={(location: AutocompleteAddress) => {
          onChangeLocation(location)
          if (!location.description) {
            setGeometry(null)
          }
        }}
        onChange={(location) => {
          onGetPlaceDetail(location)
        }}
      />

      <div className="flex flex-col gap-y-2">
        <span className="text-white text-sm font-bold">{t('common.distance')}</span>
        <div className="flex justify-between items-center gap-x-4 pt-12">
          <div className="w-full">
            <RangeSlider className="h-4" min={0} max={100} value={distance} onChange={onChangeDistance} />
            <div className="flex justify-between mt-1">
              <span className="text-white text-xs font-medium text-opacity-80">0</span>
              <span className="text-white text-xs font-medium text-opacity-80">100</span>
            </div>
          </div>
          <span className="text-white text-xs font-medium w-7">{t('common.mi')}</span>
        </div>
      </div>
    </div>
  )
}
